import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { UntypedFormControl, ValidatorFn, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { ICellEditorAngularComp } from 'ag-grid-angular';

import { UserFriendlyError } from 'models';
import { NotificationService } from 'src/app/services/notification.service';

import { FieldBaseComponent } from '../field-components/field-component.base.component';

import { UntilDestroy } from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import { MiniFabDirective } from 'src/app/directives/mini-fab/mini-fab.directive';
import { FieldCellEditorParameters } from './field-cell-editor-parameters';

/** Field cell editor base component. */
@UntilDestroy({ checkProperties: true })
@Component({
  template: '',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  hostDirectives: [
    {
      directive: MiniFabDirective,
      outputs: ['close', 'moveright', 'movedown'],
      inputs: ['mini-fab-disabled'],
    },
  ],
})
export class FieldCellEditorBaseComponent
  implements ICellEditorAngularComp, AfterViewInit, OnInit
{
  /** Field component reference. */
  @ViewChild('fieldComponent')
  fieldComponent: FieldBaseComponent;
  /** Cell Parameters. */
  cellParams: FieldCellEditorParameters;
  /** Form control. */
  formControl: UntypedFormControl;

  /** miniFab output subscriptions. */
  miniFabCloseSubscription?: Subscription;
  miniFabMoveRightSubscription?: Subscription;
  miniFabMoveDownSubscription?: Subscription;

  // Injected MiniFabDirective from host
  private miniFabDirective = inject(MiniFabDirective, { host: true });

  /**
   * Whether the cell editor is a popup.
   *
   * @returns True if the cell editor is a popup.
   */
  get isCellEditorPopup(): boolean {
    return !!this.cellParams.colDef.cellEditorPopup;
  }

  constructor(
    protected translate: TranslocoService,
    protected notifications: NotificationService
  ) {}

  // All commented functions in this file are things that are overloadable functions provided by AG-Grid.
  // They are here for easy access to their availability in the future.

  // /** @inheritdoc */
  // afterGuiAttached?(): void {
  //   throw new Error('Method not implemented.');
  // }

  /** @inheritdoc */
  agInit(params: FieldCellEditorParameters): void {
    this.cellParams = params;
    const validators: ValidatorFn[] = [];
    if (params.field.required) {
      validators.push(Validators.required);
    }

    this.formControl = new UntypedFormControl(params.value, validators);
  }

  /** @inheritdoc */
  focusIn?(): void {
    this.focus();
  }

  // /** @inheritdoc */
  // focusOut?(): void {
  //   throw new Error('Method not implemented.');
  // }

  // /** @inheritdoc */
  // getPopupPosition?(): string | undefined {
  //   throw new Error('Method not implemented.');
  // }

  /** @inheritdoc */
  getValue() {
    return this.formControl.value;
  }

  /** @inheritdoc */
  isCancelAfterEnd?(): boolean {
    if (this.getValue() !== this.cellParams.value && this.formControl.invalid) {
      this.showError();
    } else if (this.cellParams.field.required && !this.getValue()) {
      this.showError();
    }
    return this.formControl.invalid;
  }

  // /** @inheritdoc */
  // isCancelBeforeStart?(): boolean {
  //   throw new Error('Method not implemented.');
  // }

  // /** @inheritdoc */
  // isPopup?(): boolean {
  //   throw new Error('Method not implemented.');
  // }

  ngAfterViewInit(): void {
    window.setTimeout(() => {
      if (this.cellParams.cellStartedEdit) {
        this.focus();
      }
    });
  }

  ngOnInit(): void {
    this.subscribeToMiniFabEvents();

    // Disabled the mini FAB controls if the grid is using fullRow editing.
    const fullRowEditing =
      this.cellParams.api.getGridOption('editType') === 'fullRow';
    this.miniFabDirective.disabled = fullRowEditing;
  }

  /** Subscribes to mini-fab events. */
  private subscribeToMiniFabEvents() {
    this.miniFabCloseSubscription = this.miniFabDirective.close.subscribe(() =>
      this.onMiniFabClose()
    );
    this.miniFabMoveRightSubscription =
      this.miniFabDirective.moveright.subscribe(() =>
        this.onMiniFabMoveRight()
      );
    this.miniFabMoveDownSubscription = this.miniFabDirective.movedown.subscribe(
      () => this.onMiniFabMoveDown()
    );
  }

  /** Shows an error when a field cell has failed validation. This also flashes the cell. */
  showError(): void {
    const error: UserFriendlyError = {
      error: undefined,
      description: `The value ${this.formControl.value} is not valid for the ${this.cellParams.field.name} field. The field will not be updated.`,
      i18n: 'INVALID_FIELD_VALUE',
      i18nParameters: {
        fieldValue: this.formControl.value ?? '',
        fieldName: this.cellParams.field.name,
      },
    };
    this.notifications.error(error);
    this.cellParams.api.flashCells({
      rowNodes: [this.cellParams.node],
      columns: [this.cellParams.column],
    });
  }

  private focus(): void {
    this.fieldComponent.focus();
  }

  // Handler for close button: stop editing
  onMiniFabClose(): void {
    try {
      this.cellParams.api.stopEditing();
    } catch (e) {
      // ignore
    }
  }

  // Handler for moveright: try to tab to next cell
  onMiniFabMoveRight(): void {
    try {
      // Attempt to call ag-grid's tabToNextCell; if not available, fall back to stopEditing
      const api = this.cellParams.api;
      api.tabToNextCell();
    } catch (e) {
      // ignore
    }
  }

  // Handler for movedown: try to move focus to the cell below
  onMiniFabMoveDown(): void {
    try {
      // Get the cell API.
      const api = this.cellParams.api;
      // Get the current column and row.
      const colAccessor = this.cellParams.column;
      const currentCol = colAccessor.getColId();
      let nextRow = (this.cellParams.node.rowIndex ?? 0) + 1;
      // Check if there is a cell below to edit.
      const rowCount = this.cellParams.api.getDisplayedRowCount();
      if (nextRow >= rowCount) {
        // Create a new row if there was no next row..
        const transaction = this.cellParams.api.applyTransaction({
          add: [{}],
        });
        // Use it as the nextRow value, so if it wasn't at the bottom due to
        // sorting we will get the new row.
        if (transaction && transaction.add && transaction.add.length > 0) {
          const newRowNode = transaction.add[0];
          const newRowIndex = this.cellParams.api.getRowNode(
            newRowNode.id!
          )?.rowIndex;
          if (typeof newRowIndex === 'number') {
            // Use the new row index.
            nextRow = newRowIndex;
          }
        }
      }
      // Open the cell editor in the cell below.
      api.startEditingCell({
        rowIndex: nextRow,
        colKey: currentCol,
      });
    } catch (e) {
      // ignore
    }
  }
}
